import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading, Marquee } from "@/components/shared/SectionHeading";
import { CTABand } from "@/components/shared/CTABand";
import { PRODUCTS } from "@/data/products";
import { INDUSTRIES } from "@/data/industries";
import { SERVICES, PROCESS_STEPS } from "@/data/services";
import { ELASTOMERS } from "@/data/elastomers";
import { useSEO, ORG_JSONLD } from "@/lib/seo";

const RollerScene = lazy(() => import("@/components/three/RollerScene"));

const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section className="relative min-h-[92vh] flex items-end bg-ink bg-blueprint overflow-hidden border-b border-line">
      <div className="absolute inset-y-0 right-0 w-full lg:w-[58%] opacity-40 lg:opacity-100" aria-hidden="true">
        {!reduce && (
          <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center"><img src="/images/banner_roler.png" alt="" className="w-3/4 opacity-40" /></div>}>
            <RollerScene />
          </Suspense>
        )}
        {reduce && <img src="/images/banner_roler.png" alt="" className="absolute top-1/2 right-8 -translate-y-1/2 w-3/4 opacity-50" />}
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 w-full pt-36 pb-16 md:pb-24 pointer-events-none">
        <Reveal y={0} delay={0.05}>
          <p className="font-mono-tech text-[11px] md:text-xs uppercase tracking-[0.3em] text-amber-brand mb-6">
            Vikas Rolls Private Limited — Est. 1970 — Bahadurgarh, India
          </p>
        </Reveal>
        <h1 className="font-display font-black tracking-tight text-[13vw] sm:text-6xl lg:text-[5.6rem] leading-[0.95] text-steel-light">
          <MaskedLines lines={["Precision Rolls.", "Engineered for"]} delay={0.15} />
          <MaskedLines lines={["Performance."]} delay={0.45} lineClassName="text-amber-brand" />
        </h1>
        <Reveal delay={0.7} y={20}>
          <p className="mt-8 max-w-xl text-steel text-base md:text-lg leading-relaxed">
            Vikas Rolls manufactures and refurbishes industrial rolls engineered for demanding
            production environments. From material selection and compound development to machining,
            covering and precision finishing, every roll is built around your process.
          </p>
        </Reveal>
        <Reveal delay={0.85} y={20}>
          <div className="mt-10 flex flex-wrap gap-4 pointer-events-auto">
            <Link
              to="/request-a-quote"
              data-testid="hero-quote-button"
              className="group inline-flex items-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 text-base md:text-lg hover:bg-steel-light"
            >
              Request a Quote
              <ArrowRight size={19} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/technical-services"
              data-testid="hero-capabilities-button"
              className="inline-flex items-center gap-3 border border-line text-steel-light font-display font-bold px-8 py-4 text-base md:text-lg hover:border-amber-brand hover:text-amber-brand"
            >
              Explore Our Capabilities
            </Link>
          </div>
        </Reveal>
        <Reveal delay={1} y={12}>
          <p className="mt-10 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-steel/80 flex items-center gap-3">
            <span className="w-2 h-2 bg-amber-brand rotate-45 inline-block" aria-hidden="true" />
            Industrial roller expertise since 1970
          </p>
        </Reveal>
      </div>
      <div className="absolute bottom-0 inset-x-0 ticks opacity-50" aria-hidden="true" />
    </section>
  );
};

const ProductRows = () => (
  <section className="bg-ink py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHeading chapter="01" label="Product Range" title={<>Five roll families.<br />One engineering standard.</>} />
        <Reveal delay={0.15}>
          <Link to="/products" data-testid="home-all-products-link" className="group inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand hover:text-steel-light">
            View all products <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="border-t border-line">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.05} y={20}>
            <Link
              to={`/products/${p.slug}`}
              data-testid={`home-product-row-${p.slug}`}
              className="group grid grid-cols-[auto_1fr_auto] md:grid-cols-[70px_auto_1fr_140px_auto] items-center gap-5 md:gap-8 py-6 md:py-7 border-b border-line hover:bg-ink-2 px-2 md:px-4 -mx-2 md:-mx-4"
            >
              <span className="font-mono-tech text-xs text-steel/70 group-hover:text-amber-brand">{p.index}</span>
              <span className="font-display font-bold tracking-tight text-xl md:text-3xl text-steel-light group-hover:text-amber-brand col-span-1">
                {p.name}
              </span>
              <span className="hidden md:block text-sm text-steel max-w-md">{p.short}</span>
              <span className="hidden md:block overflow-hidden frame-clip border border-line">
                <img src={p.image} alt={p.name} loading="lazy" className="img-industrial w-full h-20 object-cover" />
              </span>
              <ArrowUpRight size={22} className="text-line group-hover:text-amber-brand transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IndustryGrid = () => (
  <section className="bg-ink-2 border-y border-line py-20 md:py-32 bg-blueprint">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHeading chapter="02" label="Industries Served" title={<>Wherever a roll<br />meets the process.</>} />
        <Reveal delay={0.15}>
          <Link to="/industries" data-testid="home-all-industries-link" className="group inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand hover:text-steel-light">
            Explore industries <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
        {INDUSTRIES.map((ind) => (
          <StaggerItem key={ind.slug} className="bg-ink-2">
            <Link
              to={`/industries/${ind.slug}`}
              data-testid={`home-industry-tile-${ind.slug}`}
              className="group block relative overflow-hidden h-40 md:h-48"
            >
              <img src={ind.images[0]} alt={`${ind.name} industry rolls`} loading="lazy" className="img-industrial absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-4 md:p-5">
                <span className="font-mono-tech text-[10px] text-steel/70 group-hover:text-amber-brand">{ind.index}</span>
                <span className="flex items-end justify-between gap-2">
                  <span className="font-display font-bold tracking-tight text-lg md:text-xl text-steel-light leading-tight">{ind.name}</span>
                  <ArrowUpRight size={17} className="text-line group-hover:text-amber-brand flex-none mb-1 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ProcessManifesto = () => (
  <section className="bg-ink py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <SectionHeading chapter="03" label="Engineering Process" title={<>Built around<br />your application.</>} className="mb-6" />
      <Reveal delay={0.1}>
        <p className="max-w-2xl text-steel text-base md:text-lg leading-relaxed mb-16">
          No two lines run the same. Every roll starts with your operating conditions — speed, load,
          temperature, friction and process medium — and ends with a precision-finished component
          inspected against its specification.
        </p>
      </Reveal>
      <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
        {PROCESS_STEPS.map((step) => (
          <StaggerItem key={step.step}>
            <div className="border-t-2 border-line pt-6 relative group hover:border-amber-brand transition-colors duration-300">
              <span className="font-display font-black text-6xl md:text-7xl text-transparent absolute -top-5 right-0 select-none" style={{ WebkitTextStroke: "1px #2c2f32" }} aria-hidden="true">
                {step.step}
              </span>
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-3">Step {step.step}</p>
              <h3 className="font-display font-bold text-xl md:text-2xl text-steel-light mb-3">{step.title}</h3>
              <p className="text-sm md:text-base text-steel leading-relaxed">{step.text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="bg-ink-2 border-y border-line py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-20 items-start">
      <div>
        <SectionHeading chapter="04" label="Technical Services" title={<>New manufacture.<br />Expert refurbishment.</>} className="mb-6" />
        <Reveal delay={0.1}>
          <p className="text-steel text-base md:text-lg leading-relaxed mb-8">
            Covering, machining, grinding, balancing and repair — carried out in-house so worn rolls
            return to your line restored, and new rolls arrive ready to perform.
          </p>
          <Link to="/technical-services" data-testid="home-services-link" className="group inline-flex items-center gap-3 border border-line text-steel-light font-display font-bold px-7 py-3.5 hover:border-amber-brand hover:text-amber-brand">
            All Technical Services
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.2} className="mt-10 hidden lg:block">
          <div className="corner-marks border border-line overflow-hidden max-w-sm">
            <img src="/images/repairing.jpg" alt="Roll repair work at the Vikas Rolls facility" loading="lazy" className="img-industrial w-full object-cover" />
          </div>
        </Reveal>
      </div>
      <Stagger className="grid sm:grid-cols-2 gap-px bg-line border border-line">
        {SERVICES.map((s) => (
          <StaggerItem key={s.slug} className="bg-ink-2">
            <Link to="/technical-services" data-testid={`home-service-card-${s.slug}`} className="group block p-6 md:p-7 hover:bg-ink-3 h-full">
              <span className="font-mono-tech text-[10px] text-steel/60 group-hover:text-amber-brand">{s.index}</span>
              <h3 className="font-display font-bold text-lg text-steel-light mt-2 mb-2 group-hover:text-amber-brand">{s.name}</h3>
              <p className="text-sm text-steel leading-relaxed">{s.short}</p>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ElastomerStrip = () => (
  <section className="bg-paper text-paper-ink py-20 md:py-32 bg-blueprint-light">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
        <div>
          <SectionHeading dark={false} chapter="05" label="Material Science" title={<>Fourteen elastomers.<br />Compounded in-house.</>} className="mb-6" />
          <Reveal delay={0.1}>
            <p className="text-paper-ink/75 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              Elastomer selection is driven by your operating conditions — temperature, pressure,
              load, speed, friction, chemical exposure and abrasion. Our compounding plant formulates
              each cover from the finest raw materials, tested against exacting standards.
            </p>
            <Link to="/elastomers" data-testid="home-elastomers-link" className="group inline-flex items-center gap-3 bg-paper-ink text-paper font-display font-bold px-7 py-3.5 hover:bg-amber-deep hover:text-ink">
              Explore Elastomers
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <Stagger className="flex flex-wrap gap-2.5 content-start">
          {ELASTOMERS.map((e) => (
            <StaggerItem key={e.name}>
              <span className="inline-flex items-center gap-2 border border-paper-line bg-paper-2 px-4 py-2 text-sm font-medium hover:border-amber-deep">
                <span className="font-mono-tech text-[10px] text-amber-deep">{e.code}</span>
                {e.name}
              </span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

const QualityStrip = () => (
  <section className="bg-ink py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="corner-marks border border-line overflow-hidden">
          <img src="/images/QUALITY_TEST.jpg" alt="Roll hardness testing at Vikas Rolls" loading="lazy" className="img-industrial w-full object-cover" />
        </div>
      </Reveal>
      <div>
        <SectionHeading chapter="06" label="Quality & Inspection" title={<>Measured. Tested.<br />Then dispatched.</>} className="mb-6" />
        <Reveal delay={0.1}>
          <ul className="space-y-4 mb-9">
            {[
              "ISO 9001:2015 certified quality management system",
              "Incoming raw materials checked at the source",
              "Written standards govern every process",
              "In-process and final inspection on every roll",
              "Calibrated measurement and testing equipment",
              "Certified welders across diverse alloys",
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 text-steel text-base">
                <span className="w-2 h-2 bg-amber-brand rotate-45 flex-none mt-2" aria-hidden="true" />
                {point}
              </li>
            ))}
          </ul>
          <Link to="/quality" data-testid="home-quality-link" className="group inline-flex items-center gap-3 border border-line text-steel-light font-display font-bold px-7 py-3.5 hover:border-amber-brand hover:text-amber-brand">
            Our Quality System
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  useSEO({
    title: "Precision Industrial Rolls Since 1970",
    description:
      "Vikas Rolls manufactures and refurbishes industrial rolls — rubber, polyurethane, steel, hard chromed and induction hardened — engineered for demanding production environments since 1970.",
    jsonLd: ORG_JSONLD,
  });
  return (
    <>
      <Hero />
      <Marquee items={["Steel", "Paper", "Packaging", "Leather", "Glass", "Textile", "Plywood", "Vinyl", "Printing", "Food & Beverage", "Conveyors"]} />
      <ProductRows />
      <IndustryGrid />
      <ProcessManifesto />
      <ServicesPreview />
      <ElastomerStrip />
      <QualityStrip />
      <CTABand />
    </>
  );
}
